import React, { memo, useMemo, useCallback, useEffect, useState } from 'react';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import { FlashCardBlockSettings } from './FlashCardBlockSettings';
import { tap } from 'rxjs/operators';
import { Container } from 'reactstrap';
import { FormRadioGroup, FormRadio } from '@core/components/form/FormRadio';
import { of } from 'rxjs';
import { useBloc } from '@core/utils/bloc';
import { FlashCardBlockBloc } from './FlashCardBlockBloc';
import { Button } from '@core/components/Button';
import classNames from 'classnames';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { R } from '@core/utils/r';
import './index.scss';
import { useDidMount } from '@core/utils/hooks/react';
import { HTMLService } from '@business/services';

function defineFlashCardAnswerForm() {
  return formGroup({
    option: formControl<string>({ placeholder: 'Possible Answer', required: true, initialValue: '' }),
  });
}

export function defineFlashCardForm() {
  return formGroup({
    flashCard: formControl<string>({ placeholder: 'Title', required: true, initialValue: '' }),
    flashCardInstructions: formControl<string>({ placeholder: 'Instructions', required: true, initialValue: '' }),
    flashCardInstructionDetails: formControl<string>({ placeholder: 'Instruction Details', initialValue: '' }),
    answers: formArray(() => defineFlashCardAnswerForm()),
    correntAnswer: formControl<string>({}),
    correntAnswerIndex: formControl<number>({}),
    answer: formControl<string>({}),
    incorrectAnswerResponse: formControl<string>({
      placeholder: 'Incorrect Answer Response',
      label: 'Incorrect Answer Response',
      required: true,
      initialValue: '',
    }),
  });
}
export type FlashCardForm = ReturnType<typeof defineFlashCardForm>;
export type FlashCardData = FormDataType<typeof defineFlashCardForm>;

function defineFlashCardBlockForm() {
  return formGroup({
    order: formControl<string>({
      initialValue: 'random',
    }),
    mainInstructions: formControl<string>({
      initialValue: '',
      label: 'Instructions',
      required: true,
    }),
    flashCards: formArray(() => defineFlashCardForm()),
    completionMessage: formControl<string>({
      initialValue: '',
      label: 'Completion Message',
      required: true,
    }),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type FlashCardWidgetFormData = FormDataType<typeof defineFlashCardBlockForm>;
export type FlashCardWidgetForm = ReturnType<typeof defineFlashCardBlockForm>;

export enum FlashCardBlockVariation {
  flashCard = 'flashCard',
}

export const FlashCardBlock = memo((props: CourseBuilder.BlockProps<FlashCardBlockVariation>) => {
  const { variation, preview, lessonId } = props;
  const isEditable = props.editable;
  const editable = useMemo(() => !!isEditable || !!preview, [isEditable, preview]);
  const form = useMemo(() => defineFlashCardBlockForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(FlashCardBlockBloc, lessonId);

  const isRandom = useMemo(() => formValue.order === 'random', [formValue.order]);

  const flashCard = useObservable(form.controls.flashCards.items$, []);
  const flashCardItems = useMemo(() => (isRandom ? R.shuffle(flashCard) : flashCard), [isRandom, flashCard]);

  const [activeIndex, setActiveIndex] = useState(-1);
  const activeFlashCard = useMemo(() => R.get(flashCardItems, activeIndex), [activeIndex, flashCardItems]);
  const isDrillCompleted = useMemo(
    () => activeIndex === flashCard.length && !R.some(formValue.flashCards, s => R.isEmpty(s.answer)),
    [activeIndex, formValue.flashCards, flashCard.length],
  );
  const isDrillAllCorrect = useMemo(() => R.every(formValue.flashCards, s => s.answer === s.correntAnswer), [formValue.flashCards]);
  const [haveWrongAnswer, setWrongAnswer] = useState(false);

  useDidMount(() => {
    if (props.preview && !formValue.flashCards.length) {
      form.patchValue({
        mainInstructions: 'Instruction',
        flashCards: [
          {
            flashCard: 'Title FlashCard 1',
            flashCardInstructions: 'Instructions',
            flashCardInstructionDetails: 'Instruction Details',
            answers: [{ option: 'Answer 1' }, { option: 'Answer 2' }, { option: 'Answer 3' }],
            correntAnswer: 'Answer 1',
            correntAnswerIndex: 0,
            answer: '',
            incorrectAnswerResponse: 'Incorrect Answer Response',
          },
        ],
        completionMessage: 'Completion Success',
      });
    }
  });

  const onResetDrill = useCallback(() => {
    const newFlashCards = formValue.flashCards.map(s => ({ ...s, answer: '' }));
    setWrongAnswer(false);
    setActiveIndex(0);
    form.patchValue({ flashCards: isRandom ? R.shuffle(newFlashCards) : newFlashCards });
  }, [form, formValue.flashCards, isRandom]);

  useEffect(() => {
    if (activeIndex === -1 && flashCard.length > 0) {
      flashCard.forEach(s => s.controls.answer.setValue(''));
      flashCard.forEach(s =>
        s.controls.correntAnswer.setValue(
          s.controls.answers.value && s.controls.answers.value.length
            ? R.get(s.controls.answers.value, `${s.controls.correntAnswerIndex.value || 0}.option`) || ''
            : '',
        ),
      );
      setActiveIndex(0);
    }
  }, [activeIndex, flashCard, flashCard.length]);

  const onSubmit = useCallback(
    () => of(isDrillAllCorrect && isDrillCompleted && !haveWrongAnswer),
    [haveWrongAnswer, isDrillAllCorrect, isDrillCompleted],
  );

  useCourseForm(props.id, form, { onSubmit });

  const onNext = useCallback(
    (shouldMove: boolean) => {
      if (!activeFlashCard) return;
      if (props.editable || props.preview || R.isEmpty(lessonId)) return shouldMove && setActiveIndex(activeIndex + 1);
      bloc
        .onProgress(lessonId || '', {
          title: formValue.flashCards[activeIndex].flashCard,
          answer: formValue.flashCards[activeIndex].answer,
          correct: formValue.flashCards[activeIndex].correntAnswer,
          status: formValue.flashCards[activeIndex].answer == formValue.flashCards[activeIndex].correntAnswer ? 'pass' : 'fail',
        })
        .pipe(tap(() => shouldMove && setActiveIndex(activeIndex + 1)))
        .subscribe();
    },
    [activeIndex, activeFlashCard, bloc, formValue.flashCards, lessonId, props.editable, props.preview],
  );

  useConfigureBlock({
    props,
    form,
    contentComponent: FlashCardBlockSettings,
    contentProps: { form, variation },
  });

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-flash-card', variation)} advanceForm={formValue.advance}>
        <div style={{ flex: 1 }}>
          <div>
            {!!activeFlashCard && (
              <ScenarioItem
                isRandom={isRandom}
                form={activeFlashCard}
                onNext={onNext}
                onWrongAnswer={() => setWrongAnswer(true)}
                preview={!!props.preview}
                index={activeIndex + 1}
                totalQuestions={formValue.flashCards.length}
              />
            )}
            {isDrillCompleted && isDrillAllCorrect && (
              <div>
                {!haveWrongAnswer && (
                  <div className="d-flex align-items-start mt-2 flex-column">
                    <span className="text-success mb-2">{HTMLService.toReact(formValue.completionMessage)}</span>
                    <Button type="button" onClick={onResetDrill}>
                      Redo Drill
                    </Button>
                  </div>
                )}
                {haveWrongAnswer && (
                  <div className="d-flex align-items-start mt-2 flex-column">
                    <span className="text-danger mb-2">You have to get 100% correct answers in one attempt to pass this drill.</span>
                    <Button type="button" onClick={onResetDrill}>
                      Restart Drill
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </BlockContainer>
    </Container>
  );
});

interface ScenarioItemProps {
  form: FlashCardForm;
  onNext: (val: boolean) => void;
  onWrongAnswer: () => void;
  preview: boolean;
  totalQuestions: number;
  index: number;
  isRandom: boolean;
}

const ScenarioItem = memo((props: ScenarioItemProps) => {
  const { form, preview, index, totalQuestions, isRandom } = props;
  const formValue = useNonNilObservable(form.value$);
  const [shouldRetry, setShouldRetry] = useState(false);

  const onNext = useCallback(() => {
    setShouldRetry(false);
    if (shouldRetry) {
      form.patchValue({ answer: '' });
      return;
    }

    if (formValue.answer === formValue.correntAnswer) {
      props.onNext(true);
    } else {
      props.onNext(false);
      props.onWrongAnswer();
      setShouldRetry(true);
    }
  }, [form, formValue.answer, formValue.correntAnswer, props, shouldRetry]);

  return (
    <div className="mt-2">
      <span className="mr-2 font-weight-bold">
        Flash Card {index}/{totalQuestions}
        <br />
      </span>
      <div className="mr-2 mt-2">
        <span className="font-weight-bold">{formValue.flashCard}</span>
      </div>
      <div className="mr-2 mt-2">
        <span className="mb-2">{formValue.flashCardInstructions}</span>
      </div>
      <div className="mr-2 mt-2 mb-2">
        <span>{HTMLService.toReact(formValue.flashCardInstructionDetails)}</span>
      </div>
      <ScenarioDrillItem control={form} canChoose={!shouldRetry} isRandom={isRandom} />
      <div className="d-flex align-items-start mt-2 flex-column">
        {shouldRetry && <span className="mt-2 mb-2 text-danger">{HTMLService.toReact(formValue.incorrectAnswerResponse)}</span>}
        {!preview && (
          <Button style={{ marginTop: 15 }} type="button" onClick={onNext}>
            {shouldRetry ? 'Re-Take' : 'Next'}
          </Button>
        )}
      </div>
    </div>
  );
});

export interface ScenarioDrillItemProps {
  control: FlashCardForm;
  canChoose?: boolean;
  isRandom: boolean;
}

export const ScenarioDrillItem = memo((props: ScenarioDrillItemProps) => {
  const { control, canChoose = true, isRandom } = props;
  const answers = useObservable(control.controls.answers.items$, []);
  const answerItems = useMemo(() => (isRandom ? R.shuffle(answers) : answers), [answers, isRandom]);
  return (
    <FormRadioGroup control={control.controls.answer}>
      {answerItems.map((ctrl, index) => (
        <FormRadio key={index} value={ctrl.controls.option.value} disabled={!canChoose} noPadding label={ctrl.controls.option.value} course />
      ))}
    </FormRadioGroup>
  );
});
